<template>
  <div class="news-page">
    <div class="news-page-decor" aria-hidden="true" />

    <div class="news-inner container">
      <header class="news-hero" data-aos="fade-up">
        <p class="news-hero-en">LATEST NEWS</p>
        <span class="news-hero-line" />
        <h1 class="news-hero-zh">最新动态</h1>
      </header>
      <div>
        <ul v-if="pageItems.length" class="news-grid" data-aos="fade-up" data-aos-delay="60">
          <li v-for="item in pageItems" :key="item.id" class="news-grid-item">
            <RouterLink
              class="news-card"
              :to="{ name: 'news-detail', params: { id: item.id } }"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div class="news-card-media">
                <img :src="item.thumbUrl" :alt="item.title" loading="lazy" width="640" height="360" />
              </div>
              <div class="news-card-body">
                <div class="news-card-date">
                  <span class="news-card-dm">{{ formatNewsCardDate(item.date).dayMonth }}</span>
                  <span class="news-card-y">{{ formatNewsCardDate(item.date).year }}</span>
                </div>
                <h2 class="news-card-title">{{ item.title }}</h2>
                <p class="news-card-excerpt">{{ item.summary }}</p>
              </div>
            </RouterLink>
          </li>
        </ul>

        <p v-else-if="!loading" class="news-empty">暂无新闻</p>

        <nav v-if="totalPages > 1" class="news-pager" aria-label="新闻分页" data-aos="fade-up">
          <button
            type="button"
            class="news-pager-btn"
            :disabled="currentPage <= 1"
            aria-label="上一页"
            @click="goPage(currentPage - 1)"
          >
            ‹
          </button>
          <button
            v-for="p in totalPages"
            :key="p"
            type="button"
            class="news-pager-num"
            :class="{ 'is-current': p === currentPage }"
            :aria-current="p === currentPage ? 'page' : undefined"
            :aria-label="`第 ${p} 页`"
            @click="goPage(p)"
          >
            {{ p }}
          </button>
          <button
            type="button"
            class="news-pager-btn"
            :disabled="currentPage >= totalPages"
            aria-label="下一页"
            @click="goPage(currentPage + 1)"
          >
            ›
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useSeo } from '@/composables/useSeo';
import { useFrontCmsStore } from '@/stores/frontCms';
import { mapFrontNewsToCards } from '@/utils/frontCmsMappers';
import { formatNewsCardDate } from '@/data/newsCatalog';

/** 与接口默认 pageSize 一致 */
const NEWS_PAGE_SIZE = 12;

useSeo({
  title: '新闻中心',
  description: '海曦新闻中心，发布企业新闻、产品动态与行业观察。'
});

const route = useRoute();
const router = useRouter();
const cms = useFrontCmsStore();
const { newsPage, syncing } = storeToRefs(cms);

const loading = computed(() => !!syncing.value.newsPage);

const totalPages = computed(() => Math.max(1, newsPage.value.pages));

const currentPage = computed(() => {
  const fromRoute = pageFromQuery(route.query.page);
  const max = totalPages.value;
  return Math.min(Math.max(1, fromRoute), max);
});

const pageItems = computed(() => {
  if (newsPage.value.pageKey !== `${currentPage.value}:${NEWS_PAGE_SIZE}`) {
    return [];
  }
  return mapFrontNewsToCards(newsPage.value.records);
});

function pageFromQuery(raw: unknown): number {
  const v = Array.isArray(raw) ? raw[0] : raw;
  const n = Number.parseInt(String(v ?? ''), 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return n;
}

watch(
  () => currentPage.value,
  (page) => {
    cms.hydrateFromCache();
    void cms.syncNewsPage(page, NEWS_PAGE_SIZE);
  },
  { immediate: true }
);

watch(
  () => [route.query.page, totalPages.value] as const,
  () => {
    const raw = pageFromQuery(route.query.page);
    const max = totalPages.value;
    if (raw > max) {
      router.replace({
        path: '/news',
        query: max <= 1 ? {} : { page: String(max) }
      });
    }
  },
  { immediate: true }
);

function goPage(p: number) {
  const next = Math.min(Math.max(1, p), totalPages.value);
  router.replace({
    path: '/news',
    query: next <= 1 ? {} : { page: String(next) }
  });
}
</script>

<style scoped lang="scss">
.news-page {
  position: relative;
  min-height: calc(100vh - 80px);
  padding: 104px 0 72px;
  background: #fff;
  color: #1a1a1a;
  overflow: hidden;
}

.news-page-decor {
  position: absolute;
  right: -8%;
  top: 0;
  width: min(58vw, 720px);
  height: min(120vh, 1100px);
  pointer-events: none;
  background:
    repeating-linear-gradient(
      -14deg,
      transparent 0,
      transparent 22px,
      rgba(0, 0, 0, 0.028) 22px,
      rgba(0, 0, 0, 0.028) 23px
    ),
    radial-gradient(ellipse 70% 55% at 88% 12%, rgba(72, 201, 176, 0.09), transparent 58%);
  mask-image: linear-gradient(105deg, transparent 0%, #000 35%);
}

.news-inner {
  position: relative;
  z-index: 1;
  display: flex;
}

@media (min-width: 1680px) {
  .news-hero {
    margin-right: clamp(60px, calc(120 / 1900 * 100vw), 140px);
  }
}

.news-hero {
  margin-right: 120px;
  padding-top: 140px;
  width: 85px;
}

.news-hero-en {
  margin: 0 0 8px;
  color: #efefef;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  text-transform: uppercase;
}

.news-hero-line {
  display: block;
  height: 2px;
  align-self: stretch;
  border-radius: 999px;
  background: #48c9b0;
  margin: 10px 0;
}

.news-hero-zh {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;
}

.news-grid {
  list-style: none;
  margin-left: 120px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px 32px;
  width: 700px;
}

.news-grid-item {
  margin: 0;
  padding: 32px;
  border-radius: 8px;
  border: 1px solid #efefef;
  background: #fff;
  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.25s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(72, 201, 176, 0.45);
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.09);

    .news-card-media img {
      transform: scale(1.05);
    }

    .news-card-title {
      color: #0d9488;
    }

    .news-card-dm {
      color: #14b8a6;
    }
  }
}

.news-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
}

.news-card-media {
  overflow: hidden;
  background: #e5e7eb;
  margin-bottom: 32px;

  img {
    display: block;
    width: 270px;
    height: 126px;
    object-fit: cover;
    transition: transform 0.45s ease;
  }
}

.news-card-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

.news-card-date {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.news-card-dm {
  font-family: 'DIN Condensed', sans-serif;
  font-size: 24px;
  font-weight: 700;
  transition: color 0.25s ease;
}

.news-card-y {
  color: #666;
  font-family: 'DIN Condensed', sans-serif;
  font-size: 14px;
  font-weight: 700;
}

.news-card-title {
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;
  transition: color 0.25s ease;
}

.news-card-excerpt {
  margin: 0;
  overflow: hidden;
  color: #666;
  text-overflow: ellipsis;
  font-size: 14px;
  line-height: 24px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  flex: 1;
}

.news-empty {
  margin: 48px 0 48px 120px;
  color: #666;
  font-size: 14px;
}

.news-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 44px;
}

.news-pager-btn,
.news-pager-num {
  min-width: 40px;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  font-size: 15px;
  cursor: pointer;
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.news-pager-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.news-pager-num:hover,
.news-pager-btn:hover:not(:disabled) {
  border-color: #48c9b0;
  color: #0f766e;
  background: rgba(72, 201, 176, 0.08);
  box-shadow: 0 4px 12px rgba(72, 201, 176, 0.2);
  transform: translateY(-2px);
}

.news-pager-num.is-current {
  background: #48c9b0;
  border-color: #48c9b0;
  color: #ffffff;
  font-weight: 600;
}

@media (max-width: 900px) {
  .news-page {
    padding: 88px 0 88px;
  }

  .news-page-decor {
    opacity: 0.4;
    width: 70vw;
    right: -25%;
  }

  .news-inner {
    flex-direction: column;
    width: 100%;
  }

  .news-hero {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
    width: 100%;
    margin: 0 0 24px;
    padding-top: 0;
  }

  .news-hero-en {
    margin: 0;
    font-size: clamp(18px, 4.5vw, 22px);
    color: #e8e8e8;
  }

  .news-hero-line {
    width: 36px;
    margin: 0;
  }

  .news-hero-zh {
    flex: 1 0 100%;
    font-size: 15px;
    line-height: 1.5;
  }

  .news-grid {
    margin-left: 0;
    width: 100%;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .news-grid-item {
    padding: 20px;
  }

  .news-card-media {
    margin-bottom: 16px;

    img {
      width: 100%;
      height: auto;
      aspect-ratio: 16 / 9;
      max-height: 200px;
    }
  }

  .news-card-body {
    gap: 12px;
  }

  .news-empty {
    margin: 32px 0;
    text-align: center;
  }

  .news-pager {
    margin-top: 28px;
    gap: 6px;
  }

  .news-pager-btn,
  .news-pager-num {
    min-width: 36px;
    height: 36px;
    font-size: 14px;
  }
}

@media (max-width: 640px) {
  .news-page {
    padding: 80px 0 100px;
  }

  .news-page-decor {
    display: none;
  }

  .news-hero {
    margin-bottom: 20px;
  }

  .news-grid {
    gap: 12px;
  }

  .news-grid-item {
    padding: 0;
    border: none;
    border-radius: 10px;
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);

    &:hover {
      transform: none;
      box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
    }
  }

  .news-card {
    flex-direction: row;
    align-items: stretch;
    gap: 0;
  }

  .news-card-media {
    flex: 0 0 112px;
    width: 112px;
    margin: 0;

    img {
      width: 100%;
      height: 100%;
      min-height: 96px;
      max-height: none;
      aspect-ratio: auto;
      object-fit: cover;
    }
  }

  .news-card-body {
    flex: 1;
    min-width: 0;
    padding: 12px 14px;
    gap: 6px;
    justify-content: center;
  }

  .news-card-media {
    border-radius: 10px 0 0 10px;
  }

  .news-card-date {
    flex-direction: row;
    align-items: baseline;
    gap: 6px;
  }

  .news-card-dm {
    font-size: 18px;
  }

  .news-card-y {
    font-size: 12px;
  }

  .news-card-title {
    font-size: 14px;
    line-height: 1.45;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .news-card-excerpt {
    display: none;
  }

  .news-pager {
    margin-top: 24px;
    padding: 0 4px;
  }

  .news-pager-btn,
  .news-pager-num {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    font-size: 13px;
    border-radius: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .news-grid-item,
  .news-card-media img,
  .news-card-title,
  .news-card-dm,
  .news-pager-btn,
  .news-pager-num {
    transition: none;
  }

  .news-grid-item:hover,
  .news-pager-num:hover,
  .news-pager-btn:hover:not(:disabled) {
    transform: none;
  }

  .news-grid-item:hover .news-card-media img {
    transform: none;
  }
}
</style>
